import { IconButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import EditIcon from "resources/images/EditIcon.png";
import AutoComplete from "components/basic/autoComplete";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useMaskedInput } from "hooks/useMaskedInput";

export interface InlineEditRowProps {
  label: string;
  value: string;
  onSave: (val: string) => void;
  inputType?: "text" | "dropdown";
  options?: string[];
  mask?: (input: string, previous: string) => string;
  /** Display text for a stored value, e.g. its translation. */
  formatOption?: (value: string) => string;
}

const passThrough = (input: string) => input;

const InlineEditRow: React.FC<InlineEditRowProps> = ({
  label,
  value,
  onSave,
  inputType = "text",
  options = [],
  mask,
  formatOption = passThrough,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const handleMaskedChange = useMaskedInput(mask ?? passThrough);

  const handleSave = () => {
    onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  return (
    <div
      style={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: `${theme.spacing(0.75)} 0`,
      }}
    >
      {!editing ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: theme.spacing(1),
          }}
        >
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            {label}
          </Typography>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(0.5),
              minWidth: 0,
            }}
          >
            {/* Long links and addresses wrap rather than push past a phone's edge. */}
            <Typography
              variant="body2"
              style={{
                fontWeight: "bold",
                textAlign: "right",
                overflowWrap: "anywhere",
              }}
            >
              {value ? formatOption(value) : "—"}
            </Typography>

            <IconButton
              size="small"
              onClick={() => {
                setDraft(mask ? mask(value, "") : value);
                setEditing(true);
              }}
            >
              <img src={EditIcon} alt={t("ui.edit")} width={15} height={15} />
            </IconButton>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
          }}
        >
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            {label}
          </Typography>

          {inputType === "dropdown" ? (
            <AutoComplete
              name={label}
              value={options.indexOf(draft) + 1 || null}
              options={options.map((option, index) => ({
                id: index + 1,
                label: formatOption(option),
              }))}
              label={t("ui.select")}
              onChange={(option) =>
                setDraft(option ? options[option.id - 1] : "")
              }
              size="tiny"
              textAlign="left"
            />
          ) : (
            <TextField
              size="tiny"
              name={label}
              value={draft}
              placeHolder={label}
              onChange={(event) =>
                setDraft(
                  mask ? handleMaskedChange(event, draft) : event.target.value,
                )
              }
            />
          )}

          <div style={{ display: "flex", gap: theme.spacing(1) }}>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={handleSave}
              style={{ color: "white", padding: `0 ${theme.spacing(2)}` }}
            >
              {t("ui.save")}
            </Button>

            <Button size="small" variant="text" onClick={handleCancel}>
              {t("ui.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineEditRow;
